<template>
  <div style="position: relative;">
    <v-img
      v-if="sectorImage"
      :src="sectorImage.url"
      width="80"
      height="80"
      style="position: absolute; top: 20px; right: 40px; transform: translate(50%, -50%); z-index: 99"
    />
    <t-card>
      <v-toolbar
        flat
        :color="sectorColor"
        density="compact"
      >
        <v-toolbar-title class="card-title-responsive">
          Setor: {{ sector.name }}
        </v-toolbar-title>
      </v-toolbar>

      <display-adapter>
        <template #mobile>
          <v-form ref="form">
            <validation-ticket-item
              v-for="(item, index) in items"
              :key="index"
              :item="item"
              :last="index === items.length - 1"
            />
          </v-form>
        </template>
        <template #desktop>
          <v-form ref="form">
            <v-card-text class="pa-0">
              <v-table class="transparent validation-ticket-table">
                <tbody>
                  <validation-ticket-item
                    v-for="(item, index) in items"
                    :key="index"
                    :item="item"
                    :last="index === items.length - 1"
                  />
                </tbody>
              </v-table>
            </v-card-text>
          </v-form>
        </template>
      </display-adapter>
    </t-card>
  </div>
</template>

<script setup lang="ts">
import { computed, useTemplateRef } from "vue";
import type { VForm } from "vuetify/components";

import TCard from "@/components/core/TCard.vue";
import type Sector from "@/types/Sector";
import DisplayAdapter from "../core/DisplayAdapter.vue";
import ValidationTicketItem from "./ValidationTicketItem.vue";
import { useCartStore } from "@/stores/cart";
import { useEventsStore } from "@/stores/events";

interface Props {
  sector: Sector;
}

const props = defineProps<Props>();

const cart = useCartStore();
const events = useEventsStore();

defineOptions({
  name: "ValidationTicketCard",
});

// Refs
const form = useTemplateRef<VForm>("form");

// Computed properties
const items = computed(() => cart.tickets.filter(t => t.sector.id === props.sector.id));

const sectorConfigs = computed(() =>
  events.selected?.sector_configs.find(c => c.sector_id === props.sector.id),
);

const sectorColor = computed(() => sectorConfigs.value?.color ?? "primary");

const sectorImage = computed(() =>
  events.selected?.images?.find(i => i.id === sectorConfigs.value?.shield_image_id),
);

// Methods
const validate = async (): Promise<boolean> => {
  if (form.value === null) return false;

  const result = await form.value.validate();

  return result.valid;
};

// Expose methods to parent component
defineExpose({
  validate,
});
</script>

<style>
.table-header {
  font-size: 1rem !important;
}

/* Target the actual table element inside v-table's wrapper */
.v-table.validation-ticket-table > .v-table__wrapper > table {
  table-layout: fixed;
  width: 100%;
}

/* Prevent cells from expanding */
.v-table.validation-ticket-table td,
.v-table.validation-ticket-table th {
  overflow: hidden;
}

/* If you want to constrain your input fields to fixed width */
.v-table.validation-ticket-table td .v-input {
  max-width: 100%;
}
</style>
